import { createContext, useState, useContext, useEffect, ReactNode, useCallback } from 'react';
import { getUserByToken } from './core/_requests_v2';
import { APIError, useAxios, getUserAuth, removeAuthCompletely, setUserAuth, getCurrentAccountUniqueIdentifierInLocalStorage, setCurrentAccountUniqueIdentifierInLocalStorage } from './core/_axios';
import { SelectCurrentAccountUniqueIdentifer } from './routes/SelectCurrentAccountUniqueIdentifer';
import { UserFast } from './core/_models_fast';

// Define a type for the context value
interface AuthContextType {
    isAuthenticated: boolean;
    login: (apiAuthToken: string) => void;
    logout: () => void;
    loading: boolean;
    authenticationError: string;
    tryToVerifyToken: () => void;
    currentUser: UserFast | null;
    currentUserAccountUniqueIds: string[] | null;
    currentUserAccountUniqueId: string | null;
    viewingAllAccounts: boolean;
    stopViewingIndividualAccount: () => void;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define a type for the AuthProvider props
interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const axios = useAxios();

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [authenticationError, setAuthenticationError] = useState('');

    const [currentUser, setUser] = useState<UserFast | null>(null);
    const [currentUserAccountUniqueIds, setCurrentUserAccountUniqueIds] = useState<string[] | null>(null);
    const [currentUserAccountUniqueId, setCurrentUserAccountUniqueId] = useState<string | null>(null);

    const [showAccountPicker, setShowAccountPicker] = useState<boolean>(false);

    const [viewingAllAccounts, setViewingAllAccounts] = useState(false);

    const onSkippedAccountPicker = useCallback(() => {
        setLoading(false);
        setIsAuthenticated(true)
        setShowAccountPicker(false);
        setCurrentAccountUniqueIdentifierInLocalStorage('');
        setViewingAllAccounts(true);
    }, []);

    const tryToVerifyToken = useCallback(async () => {
        const handleFinishLoadingSuperSuperUser = () => {
            setIsAuthenticated(true);
            setLoading(false);
            setAuthenticationError('');
            setCurrentUserAccountUniqueIds(null);
        }

        const handleFinishedLoadingUser = (selectedAccountUniqueId: string, user: UserFast, allowedAccountUniqueIds: string[]) => {
            setIsAuthenticated(true);
            setLoading(false);
            setAuthenticationError('');
            setCurrentUserAccountUniqueIds(allowedAccountUniqueIds);
            setCurrentUserAccountUniqueId(selectedAccountUniqueId || null);
        };

        const handleErrorLoadingUser = (errorMessage: string) => {
            setIsAuthenticated(false);
            setLoading(false);
            setAuthenticationError(errorMessage);
            setUser(null);
            setCurrentUserAccountUniqueIds(null);
        }

        setLoading(true);
        setAuthenticationError('');
        const userAuth = getUserAuth()
        if (userAuth?.api_token) {
            try {
                const {
                    user,
                    allowedAccountsUniqueIds
                } = await getUserByToken(axios);
                setUser(user);
                let selectedAccountUniqueId = getCurrentAccountUniqueIdentifierInLocalStorage(); // this is a user account membership unique_identifier
                if ((user.is_super_super_user || (user.belongs_to_agency && user.is_agency_admin))) {
                    if (selectedAccountUniqueId && selectedAccountUniqueId !== '' && selectedAccountUniqueId !== 'null' && selectedAccountUniqueId !== 'undefined') {
                        setCurrentUserAccountUniqueId(selectedAccountUniqueId);
                        setViewingAllAccounts(false);
                    } else {
                        setCurrentUserAccountUniqueId(null);
                        setViewingAllAccounts(true);
                    }
                    handleFinishLoadingSuperSuperUser();
                    return
                }
                // If not a super user or in an agency, by default we only are viewing all accounts if
                // we previously were or if there is more than one available account.
                setViewingAllAccounts(
                    selectedAccountUniqueId ? false : allowedAccountsUniqueIds.length <= 1 ? false : true
                )
                // If there are no memberships (which should never happen, as the API will return an error)
                // then we still set an error.
                if (allowedAccountsUniqueIds.length === 0) {
                    setCurrentAccountUniqueIdentifierInLocalStorage('');
                    handleErrorLoadingUser('No memberships found for this user.');
                    return;
                }
                // If the user only has a single membership, we can just set it and move on
                if (allowedAccountsUniqueIds.length === 1) {
                    const selectedAccountUniqueId = allowedAccountsUniqueIds[0];
                    setCurrentAccountUniqueIdentifierInLocalStorage(selectedAccountUniqueId);
                    handleFinishedLoadingUser(selectedAccountUniqueId, user, allowedAccountsUniqueIds);
                    return
                }
                // Before we do anything we check to see if this selected membership exists
                // in the returned list. This may not be good when there are hundreds of memberships,
                // but it's important for the logic that follows.
                if (selectedAccountUniqueId) {
                    const selectedAccountIsValid = allowedAccountsUniqueIds.find((m: string) => m === selectedAccountUniqueId);
                    if (!selectedAccountIsValid) {
                        selectedAccountUniqueId = '';
                    }
                }
                // If we have a selected membership, we set it and move on
                if (selectedAccountUniqueId) {
                    setCurrentAccountUniqueIdentifierInLocalStorage(selectedAccountUniqueId);
                    handleFinishedLoadingUser(selectedAccountUniqueId, user, allowedAccountsUniqueIds);
                    return;
                }
                // Finally now we show an initial account selector.
                //setShowAccountPicker(true);
                // TODO: If we haven't ever had a chance to select an account, we should
                // show the account picker. Otherwise, we default to all accounts.
                setCurrentAccountUniqueIdentifierInLocalStorage('');
                handleFinishedLoadingUser('', user, allowedAccountsUniqueIds);
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
                console.warn('Error verifying authentication:', errorMessage);
                setAuthenticationError(errorMessage);
                setIsAuthenticated(false);
                setLoading(false);
                setUser(null);
                setCurrentUserAccountUniqueIds(null);
            }
        } else {
            setLoading(false);
            setIsAuthenticated(false);
            setAuthenticationError('');
            setUser(null);
            setCurrentUserAccountUniqueIds(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        tryToVerifyToken();
    }, [tryToVerifyToken]);

    const login = useCallback((apiAuthToken: string) => {
        setUserAuth({ api_token: apiAuthToken });
        tryToVerifyToken();
    }, [tryToVerifyToken]);

    const logout = () => {
        removeAuthCompletely();
        setIsAuthenticated(false);
        setAuthenticationError('');
        setLoading(false);
    };

    const stopViewingIndividualAccount = useCallback(() => {
        setCurrentAccountUniqueIdentifierInLocalStorage('');
        window.location.href = '/';
    }, []);

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            login,
            logout,
            loading,
            authenticationError,
            tryToVerifyToken,
            currentUser,
            currentUserAccountUniqueIds,
            currentUserAccountUniqueId,
            viewingAllAccounts,
            stopViewingIndividualAccount
        }}>
            {showAccountPicker ? <SelectCurrentAccountUniqueIdentifer
                onSkipped={onSkippedAccountPicker}
            /> : children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
